import { Component, HostListener, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { DirectorFormComponent } from '../director-form/director-form.component';
import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';
import { MaterialModule } from '../../../shared/material.module';
import { DirectorService } from '../../../services/director.service';
import { DirectorPositionsResponseDto } from '../../../models/api/directorPositionsDto';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { InfoDialogService } from '../../../services/info-dialog.service';
import { of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

interface UpdateResult {
  success: boolean;
  photoUploaded?: boolean;
  photoDeleted?: boolean;
  photoError?: any;
  photoDeleteError?: any;
}

@Component({
  selector: 'app-director-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DirectorFormComponent,
    NgxSpinnerModule,
    RouterLink,
    MaterialModule],
  templateUrl: './director-edit.component.html',
  styleUrls: ['./director-edit.component.scss']
})
export class DirectorEditComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  @ViewChild(DirectorFormComponent) directorFormComponent!: DirectorFormComponent;
  form: FormGroup;
  directorId: string | null = null;
  directorFullName: string | null = null;
  directorCode: string | null = null;
  fromCompanyId: string | null = null;
  fromCompanyName: string | null = null;
  fromYear: string | null = null;
  positions: DirectorPositionsResponseDto | null = null;
  private loadedPhotoUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private directorService: DirectorService,
    private router: Router,
    private route: ActivatedRoute,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService,
    private infoDialogService: InfoDialogService
  ) {
    this.form = this.fb.group({
      fullName: ['', Validators.required],
      yearOfBirth: ['', [Validators.min(1900), Validators.max(new Date().getFullYear())]],
      yearOfBirthConfirmed: [false],
      age: [''],
      gender: [''],
      honorificTitle: [''],
      nationality: [''],
      secondNationality: [''],
      isDeceased: [false],
      dateOfDeath: [''],
      cvSummary: [''],
      photoFileName: [''],
      profession: [''],
      industryExpertise1: [''],
      industryExpertise2: [''],
      qualification: [''],
      hasFinancialExpertise: [false],
      financialExpertise: [''],
      hasPoliticalPosition: [false],
      politicalPosition: [''],
      isCompleted: [false],
      dateCompleted: [''],
      isActive: [true],
      workExperiences: [[]],
      trainingAndEducations: [[]]
    });
  }

  hasUnsavedChanges(): boolean {
    return this.directorFormComponent?.hasUnsavedChanges() ?? false;
  }

  // Warn on browser tab close / refresh / external navigation. Browsers only allow
  // a native, non-customizable prompt here — the Material confirmation dialog cannot
  // be shown during unload. The in-app canDeactivate guard still handles SPA navigation.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      // Legacy browsers require returnValue to be set to trigger the prompt.
      event.returnValue = true;
    }
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    this.directorId = id;
    this.fromCompanyId = this.route.snapshot.queryParamMap.get('fromCompany');
    this.fromCompanyName = this.route.snapshot.queryParamMap.get('fromCompanyName');
    this.fromYear = this.route.snapshot.queryParamMap.get('fromYear');
    if (this.directorId) {
      this.directorService.getDirectorById(this.directorId).subscribe({
        next: (director) => {
          if (director) {
            // Transform work experience and training/education
            const transformedWorkExperiences = this.transformWorkExperience(director.workExperience || []);
            const transformedTrainingAndEducations = this.transformTrainingAndEducation(director.trainingOrEducation || []);

            // Transform the flat API response to objects for mat-select
            const formValue = {
              ...director,
              honorificTitle: director.honorificTitleId ? { id: director.honorificTitleId, name: director.honorificTitleName } : null,
              nationality: director.nationalityId ? { id: director.nationalityId, name: director.nationalityName } : null,
              secondNationality: director.secondNationalityId ? { id: director.secondNationalityId, name: director.secondNationalityName } : null,
              profession: director.professionId ? { id: director.professionId, name: director.professionName } : null,
              industryExpertise1: director.industryExpertise1Id ? { id: director.industryExpertise1Id, name: director.industryExpertise1Name } : null,
              industryExpertise2: director.industryExpertise2Id ? { id: director.industryExpertise2Id, name: director.industryExpertise2Name } : null,
              qualification: director.qualificationId ? { id: director.qualificationId, name: director.qualificationName } : null,
              financialExpertise: director.financialExpertiseId ? { id: director.financialExpertiseId, name: director.financialExpertiseName } : null,
              politicalPosition: director.politicalPositionId ? { id: director.politicalPositionId, name: director.politicalPositionName } : null,
              // Transform work experience from flat to nested structure
              workExperiences: transformedWorkExperiences,
              // Transform training and education from flat to nested structure
              trainingAndEducations: transformedTrainingAndEducations
            };

            // Patch form without emitting events to prevent age/yearOfBirth sync
            // from incorrectly recalculating values during initial load
            this.directorFullName = director.fullName || null;
            this.directorCode = director.code || null;
            this.form.patchValue(formValue, { emitEvent: false });

            // Manually trigger update of age field state and arrays after patching
            // Use setTimeout to ensure ViewChild is available
            setTimeout(() => {
              if (this.directorFormComponent) {
                this.directorFormComponent.updateAgeFieldState();
                // Manually update arrays since patchValue was called with emitEvent: false
                this.directorFormComponent.workExperiences = transformedWorkExperiences;
                this.directorFormComponent.trainingAndEducations = transformedTrainingAndEducations;
              }
            }, 0);

            // Load the director's photo if it exists
            if (director.photoFileName) {
              this.loadDirectorPhoto();
            }

            // Load positions data
            this.loadDirectorPositions();
          }
        },
        error: (error: Error) => console.error('Error loading director:', error)
      });
    }
  }

  ngOnDestroy() {
    // Clean up the photo URL to prevent memory leaks
    if (this.loadedPhotoUrl) {
      URL.revokeObjectURL(this.loadedPhotoUrl);
    }
  }

  private loadDirectorPhoto() {
    if (this.directorId) {
      this.directorService.getDirectorPhoto(this.directorId).subscribe({
        next: (photoBlob) => {
          // Check if the blob has content (not empty)
          if (photoBlob && photoBlob.size > 0) {
            // Create an object URL from the blob
            const photoUrl = URL.createObjectURL(photoBlob);
            this.loadedPhotoUrl = photoUrl;

            // Set the photo URL in the form component
            // We need to wait for the view to be initialized
            setTimeout(() => {
              if (this.directorFormComponent) {
                this.directorFormComponent.existingPhotoUrl = photoUrl;
                this.directorFormComponent.selectedPhotoName = 'Current Photo';
              }
            }, 0);
          }
        },
        error: (error) => {
          // Photo not found or error loading - this is not critical
        }
      });
    }
  }

  private loadDirectorPositions() {
    if (!this.directorId) return;

    this.directorService.getDirectorPositions(this.directorId).subscribe({
      next: (positions) => {
        this.positions = positions;
      },
      error: (error) => {
        console.error('Error loading director positions:', error);
      }
    });
  }

  private transformWorkExperience(workExperienceFlat: any[]): any[] {
    return workExperienceFlat.map(we => {
      const { company, companyType } = this.getCompanyFromWorkExperience(we);
      return {
        id: we.id,
        company,
        companyType,
        position: we.jobPositionId ? { id: we.jobPositionId, name: we.jobPositionName } : null,
        startDate: we.from,
        endDate: we.until ?? null,
        isPresent: !!we.isPresent
      };
    });
  }

  private getCompanyFromWorkExperience(we: any): { company: any; companyType: string } {
    // Priority: opbCompanyId > companyId > workExperienceCompanyId > auditFirmId > largeShareholderId
    if (we.opbCompanyId) {
      return { company: { id: we.opbCompanyId, name: we.opbCompanyName }, companyType: 'opbCompany' };
    } else if (we.companyId) {
      return { company: { id: we.companyId, name: we.companyName }, companyType: 'company' };
    } else if (we.workExperienceCompanyId) {
      return { company: { id: we.workExperienceCompanyId, name: we.workExperienceCompanyName }, companyType: 'workExperienceCompany' };
    } else if (we.auditFirmId) {
      return { company: { id: we.auditFirmId, name: we.auditFirmName }, companyType: 'auditFirm' };
    } else if (we.largeShareholderId) {
      return { company: { id: we.largeShareholderId, name: we.largeShareholderName }, companyType: 'largeShareholder' };
    }
    return { company: null, companyType: '' };
  }

  private transformTrainingAndEducation(trainingFlat: any[]): any[] {
    return trainingFlat.map(te => ({
      id: te.id,
      type: te.type,
      educationField: te.educationFieldId ? { id: te.educationFieldId, name: te.educationFieldName } : null,
      university: te.universityId ? { id: te.universityId, name: te.universityName } : null
    }));
  }

  onSubmit() {
    this.spinnerService.show();
    if (this.form.valid && this.directorId) {
      setTimeout(() => {
        const formValue = this.form.value;

        // Map form values to UpdateDirectorRequest
        // Extract IDs from objects or use direct ID values
        const updateRequest = {
          fullName: formValue.fullName,
          yearOfBirth: formValue.yearOfBirth,
          yearOfBirthConfirmed: formValue.yearOfBirthConfirmed,
          honorificTitleId: formValue.honorificTitle?.id || formValue.honorificTitle || null,
          gender: formValue.gender,
          nationalityId: formValue.nationality?.id || formValue.nationality,
          secondNationalityId: formValue.secondNationality?.id || formValue.secondNationality || null,
          isDeceased: formValue.isDeceased,
          dateOfDeath: formValue.dateOfDeath,
          professionId: formValue.profession?.id || formValue.profession || null,
          industryExpertise1Id: formValue.industryExpertise1?.id || formValue.industryExpertise1 || null,
          industryExpertise2Id: formValue.industryExpertise2?.id || formValue.industryExpertise2 || null,
          qualificationId: formValue.qualification?.id || formValue.qualification || null,
          hasFinancialExpertise: formValue.hasFinancialExpertise,
          financialExpertiseId: formValue.financialExpertise?.id || formValue.financialExpertise || null,
          hasPoliticalPosition: formValue.hasPoliticalPosition,
          politicalPositionId: formValue.politicalPosition?.id || formValue.politicalPosition || null,
          isCompleted: formValue.isCompleted,
          dateCompleted: formValue.dateCompleted,
          cvSummary: formValue.cvSummary,
          workExperience: this.transformWorkExperienceToFlat(formValue.workExperiences || []),
          trainingOrEducation: this.transformTrainingAndEducationToFlat(formValue.trainingAndEducations || [])
        };

        this.directorService.updateDirector(this.directorId!, updateRequest).pipe(
          switchMap(() => {
            // Check if photo was removed
            const photoWasRemoved = this.directorFormComponent?.wasPhotoRemoved();
            const photoFile = this.directorFormComponent?.getPhotoFromForm();

            if (photoWasRemoved && this.directorId) {
              // Delete the photo first
              return this.directorService.deleteDirectorPhoto(this.directorId).pipe(
                switchMap(() => {
                  // After deleting, check if there's a new photo to upload
                  if (photoFile && this.directorId) {
                    return this.directorService.uploadDirectorPhoto(this.directorId, photoFile).pipe(
                      switchMap(() => of<UpdateResult>({ success: true, photoDeleted: true, photoUploaded: true })),
                      catchError((error) => {
                        console.error('Error uploading photo:', error);
                        return of<UpdateResult>({ success: true, photoDeleted: true, photoUploaded: false, photoError: error });
                      })
                    );
                  }
                  return of<UpdateResult>({ success: true, photoDeleted: true, photoUploaded: false });
                }),
                catchError((error) => {
                  console.error('Error deleting photo:', error);
                  return of<UpdateResult>({ success: true, photoDeleted: false, photoDeleteError: error });
                })
              );
            } else if (photoFile && this.directorId) {
              // Upload the photo
              return this.directorService.uploadDirectorPhoto(this.directorId, photoFile).pipe(
                switchMap(() => of<UpdateResult>({ success: true, photoUploaded: true })),
                catchError((error) => {
                  console.error('Error uploading photo:', error);
                  return of<UpdateResult>({ success: true, photoUploaded: false, photoError: error });
                })
              );
            }
            return of<UpdateResult>({ success: true, photoUploaded: false });
          }),
          catchError((error) => {
            console.error('Error updating director:', error);
            this.spinnerService.hide();
            this.infoDialogService.openErrorDialog(
              'Failed to update director. Please try again.',
              'Update Error'
            );
            return of<UpdateResult>({ success: false });
          })
        ).subscribe({
          next: (result) => {
            this.spinnerService.hide();
            if (result.success) {
              // Reset the photo removed flag after successful update
              this.directorFormComponent?.resetPhotoRemovedFlag();
              // The changes are persisted, so navigating away below must not trip
              // the unsaved-changes guard.
              this.form.markAsPristine();

              if (result.photoDeleted && result.photoUploaded) {
                this.toast.success('Director updated, photo replaced successfully', {
                  iconTheme: {
                    primary: 'var(--mat-sys-primary)',
                    secondary: '#FFF',
                  }
                });
              } else if (result.photoDeleted) {
                this.toast.success('Director updated, photo removed successfully', {
                  iconTheme: {
                    primary: 'var(--mat-sys-primary)',
                    secondary: '#FFF',
                  }
                });
              } else if (result.photoUploaded) {
                this.toast.success('Director and photo updated successfully', {
                  iconTheme: {
                    primary: 'var(--mat-sys-primary)',
                    secondary: '#FFF',
                  }
                });
              } else if (result.photoError) {
                this.toast.warning('Director updated, but photo upload failed', {
                  iconTheme: {
                    primary: '#ff9800',
                    secondary: '#FFF',
                  }
                });
              } else if (result.photoDeleteError) {
                this.toast.warning('Director updated, but photo deletion failed', {
                  iconTheme: {
                    primary: '#ff9800',
                    secondary: '#FFF',
                  }
                });
              } else {
                this.toast.success('Director updated successfully', {
                  iconTheme: {
                    primary: 'var(--mat-sys-primary)',
                    secondary: '#FFF',
                  }
                });
              }
              this.navigateBack();
            }
          }
        });
      }, 1500);
    }
  }

  private transformWorkExperienceToFlat(workExperiences: any[]): any[] {
    return workExperiences.map(we => {
      // Initialize all company fields as null
      const flatWe: any = {
        id: we.id,
        directorId: this.directorId,
        companyId: null,
        companyName: null,
        opbCompanyId: null,
        opbCompanyName: null,
        workExperienceCompanyId: null,
        workExperienceCompanyName: null,
        auditFirmId: null,
        auditFirmName: null,
        largeShareholderId: null,
        largeShareholderName: null,
        jobPositionId: we.position?.id || null,
        jobPositionName: we.position?.name || null,
        from: we.startDate || null,
        until: we.isPresent ? null : (we.endDate || null),
        isPresent: !!we.isPresent
      };

      // Set the appropriate company fields based on companyType
      if (we.company && we.companyType) {
        switch (we.companyType) {
          case 'opbCompany':
            flatWe.opbCompanyId = we.company.id;
            flatWe.opbCompanyName = we.company.name;
            break;
          case 'company':
            flatWe.companyId = we.company.id;
            flatWe.companyName = we.company.name;
            break;
          case 'workExperienceCompany':
            flatWe.workExperienceCompanyId = we.company.id;
            flatWe.workExperienceCompanyName = we.company.name;
            break;
          case 'auditFirm':
            flatWe.auditFirmId = we.company.id;
            flatWe.auditFirmName = we.company.name;
            break;
          case 'largeShareholder':
            flatWe.largeShareholderId = we.company.id;
            flatWe.largeShareholderName = we.company.name;
            break;
        }
      }

      return flatWe;
    });
  }

  private transformTrainingAndEducationToFlat(trainingAndEducations: any[]): any[] {
    return trainingAndEducations.map(te => ({
      id: te.id,
      directorId: this.directorId,
      educationFieldId: te.educationField?.id || null,
      educationFieldName: te.educationField?.name || null,
      universityId: te.university?.id || null,
      universityName: te.university?.name || null,
      type: te.type
    }));
  }

  onCancel() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.fromCompanyId) {
      this.router.navigate(['/companies/edit', this.fromCompanyId], { queryParams: { tab: 'board', ...(this.fromYear && { year: this.fromYear }) } });
    } else {
      this.router.navigate(['/directors/list']);
    }
  }
}
